/**
 * Calculate returns for Bridge/Acres dashboard overview
 *
 * BACKGROUND:
 * The dashboard displays monthly returns for investors. Returns come from two sources:
 * - Dividends: Earned from money market funds, recorded in the current month
 * - Interest: Earned from treasury investments, recorded in the previous month
 *   (due to settlement timing - treasuries mature and interest is calculated at month-end)
 *
 * INVESTMENT SCENARIOS:
 * 1. Money market only → Has dividends, no interest (null)
 * 2. Treasuries only → Has interest, no dividends (null)
 * 3. Mixed portfolio → Has both dividends and interest
 *
 * BUG FIX (Jan 2026):
 * The original implementation required dividends to be non-null to display returns.
 * This caused the dashboard to show "Pending" for treasury-only investors (like Acres)
 * who have interest but no dividends. Fixed by treating null values as 0.
 */

/**
 * Monthly return data from the custodian API
 * Each record represents one month's earnings and fees
 */
export interface MonthlyReturnData {
  start_date: string; // Format: 'YYYY-MM-DD', first day of month
  end_date: string; // Format: 'YYYY-MM-DD', last day of month
  dividends: number | null; // Money market earnings (null if no money market holdings)
  interest: number | null; // Treasury earnings (null if no treasury holdings)
  fees: number | null; // Platform fees for the month
}

/**
 * Calculated return values for dashboard display
 */
export interface CalculatedReturns {
  showPending: boolean; // True if no data available yet (show "Pending" in UI)
  returnsGross: number; // Total earnings before fees (dividends + interest)
  returnsNet: number; // Earnings after fees (gross - fees)
  returnsFee: number; // Fees charged for the month
}

/**
 * Calculate returns from monthly return data
 *
 * IMPORTANT: Dividends come from current month, but interest comes from PREVIOUS month.
 * This is because treasury interest is calculated at month-end and appears in the
 * following month's data due to settlement timing.
 *
 * @param thisMonthReturnsData - Current month's return data (provides dividends and fees)
 * @param lastMonthReturnsData - Previous month's return data (provides interest)
 * @returns Calculated returns with showPending flag
 *
 * @example
 * // Treasury-only investor (Acres)
 * calculateReturns(
 *   { dividends: null, interest: 5960, fees: 411 },  // Dec (current)
 *   { dividends: null, interest: 7668, fees: 164 }   // Nov (previous)
 * )
 * // Returns: { showPending: false, returnsGross: 7668, returnsNet: 7256.41, returnsFee: 411 }
 * // Note: Uses Nov's interest (7668), not Dec's interest
 */
export function calculateReturns(
  thisMonthReturnsData: MonthlyReturnData | undefined,
  lastMonthReturnsData: MonthlyReturnData | undefined,
): CalculatedReturns {
  let showPending = true;
  let returnsGross = 0;
  let returnsNet = 0;
  let returnsFee = 0;

  // Only show values if we have a record for the current month
  // The existence of a record (even with null values) means data has been processed
  // No record = data not yet available = show "Pending"
  if (thisMonthReturnsData) {
    // Treat null as 0 - this is the key fix for treasury-only investors
    // who have null dividends but valid interest
    const dividends = Number(thisMonthReturnsData?.dividends || 0);
    const interest = Number(lastMonthReturnsData?.interest || 0);
    const fees = Number(thisMonthReturnsData?.fees || 0);

    returnsGross = dividends + interest;
    returnsNet = returnsGross - fees;
    returnsFee = fees;
    showPending = false;
  }

  return { showPending, returnsGross, returnsNet, returnsFee };
}
